import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Clock, Eye, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { toast } from 'sonner';

const AdminDisputesPage = () => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [resolution, setResolution] = useState({
    resolution: 'refund_customer',
    resolution_notes: '',
    refund_amount: null,
    compensation_amount: null
  });

  useEffect(() => {
    loadDisputes();
  }, [filter]);

  const loadDisputes = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/pickup/admin/disputes${filter !== 'all' ? `?status=${filter}` : ''}`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Failed to load disputes');

      const data = await response.json();
      setDisputes(data.disputes || []);
    } catch (error) {
      console.error('Load disputes error:', error);
      toast.error('Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveDispute = async () => {
    if (!resolution.resolution_notes.trim()) {
      toast.error('Please provide resolution notes');
      return;
    }

    setResolving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/pickup/admin/disputes/${selectedDispute.id}/resolve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            dispute_id: selectedDispute.id,
            ...resolution
          })
        }
      );

      if (!response.ok) throw new Error('Failed to resolve dispute');

      toast.success('Dispute resolved successfully');
      setSelectedDispute(null);
      loadDisputes();
    } catch (error) {
      console.error('Resolve dispute error:', error);
      toast.error('Failed to resolve dispute');
    } finally {
      setResolving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: <Clock className="h-3 w-3" /> },
      under_review: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: <Eye className="h-3 w-3" /> },
      resolved: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: <CheckCircle className="h-3 w-3" /> },
      rejected: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: <XCircle className="h-3 w-3" /> }
    };

    const config = statusConfig[status] || statusConfig.open;

    return (
      <Badge className={config.color}>
        {config.icon}
        <span className="ml-1 capitalize">{status.replace('_', ' ')}</span>
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dispute Resolution
          </h1>
          <p className="text-muted-foreground mt-2">
            Review and resolve customer and driver disputes
          </p>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <Button
            variant={filter === 'open' ? 'default' : 'outline'}
            onClick={() => setFilter('open')}
          >
            <Clock className="h-4 w-4 mr-2" />
            Open
          </Button>
          <Button
            variant={filter === 'under_review' ? 'default' : 'outline'}
            onClick={() => setFilter('under_review')}
          >
            <Eye className="h-4 w-4 mr-2" />
            Under Review
          </Button>
          <Button
            variant={filter === 'resolved' ? 'default' : 'outline'}
            onClick={() => setFilter('resolved')}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Resolved
          </Button>
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
        </div>

        {/* Disputes List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
          </div>
        ) : disputes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No disputes found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {disputes.map((dispute) => (
              <Card key={dispute.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-3">
                      {/* Header */}
                      <div className="flex items-center gap-3">
                        {getStatusBadge(dispute.status)}
                        <Badge variant="outline" className="capitalize">
                          {dispute.dispute_type.replace('_', ' ')}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          Filed by {dispute.filed_by_role}
                        </span>
                      </div>

                      {/* Delivery Info */}
                      <div className="grid md:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Delivery ID:</span>
                          <span className="ml-2 font-medium">{dispute.delivery_id}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Amount:</span>
                          <span className="ml-2 font-medium">
                            {dispute.currency} {dispute.delivery_fee}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Payment Status:</span>
                          <span className="ml-2 font-medium capitalize">{dispute.payment_status}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Filed:</span>
                          <span className="ml-2 font-medium">
                            {new Date(dispute.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                        <p className="text-sm font-medium mb-1">Issue Description:</p>
                        <p className="text-sm text-muted-foreground">{dispute.description}</p>
                      </div>

                      {/* Evidence */}
                      {dispute.evidence_urls && dispute.evidence_urls.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Evidence:</p>
                          <div className="space-y-1">
                            {dispute.evidence_urls.map((url, index) => (
                              <a
                                key={index}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline block"
                              >
                                📎 Evidence {index + 1}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Resolution */}
                      {dispute.resolution && (
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                          <p className="text-sm font-medium text-green-800 dark:text-green-400 mb-1">
                            Resolution: {dispute.resolution.replace('_', ' ')}
                          </p>
                          <p className="text-sm text-green-700 dark:text-green-500">
                            {dispute.resolution_notes}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Action Button */}
                    {dispute.status === 'open' && (
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedDispute(dispute);
                          setResolution({
                            resolution: 'refund_customer',
                            resolution_notes: '',
                            refund_amount: dispute.delivery_fee,
                            compensation_amount: null
                          });
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Review
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Resolution Modal */}
        {selectedDispute && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>Resolve Dispute</CardTitle>
                <CardDescription>
                  Delivery {selectedDispute.delivery_id} - {selectedDispute.dispute_type}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Resolution Type */}
                <div className="space-y-3">
                  <Label>Resolution Action *</Label>
                  <div className="grid md:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setResolution({ ...resolution, resolution: 'refund_customer' })}
                      className={`p-4 text-left border-2 rounded-lg ${
                        resolution.resolution === 'refund_customer'
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="font-medium">Refund Customer</div>
                      <div className="text-xs text-muted-foreground">Full/partial refund</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setResolution({ ...resolution, resolution: 'compensate_driver' })}
                      className={`p-4 text-left border-2 rounded-lg ${
                        resolution.resolution === 'compensate_driver'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="font-medium">Compensate Driver</div>
                      <div className="text-xs text-muted-foreground">Release payment to driver</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setResolution({ ...resolution, resolution: 'reject_dispute' })}
                      className={`p-4 text-left border-2 rounded-lg ${
                        resolution.resolution === 'reject_dispute'
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="font-medium">Reject Dispute</div>
                      <div className="text-xs text-muted-foreground">Dispute not valid</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setResolution({ ...resolution, resolution: 'split_compensation' })}
                      className={`p-4 text-left border-2 rounded-lg ${
                        resolution.resolution === 'split_compensation'
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="font-medium">Split Compensation</div>
                      <div className="text-xs text-muted-foreground">Custom split</div>
                    </button>
                  </div>
                </div>

                {/* Amounts */}
                {resolution.resolution === 'refund_customer' && (
                  <div className="space-y-2">
                    <Label>Refund Amount ({selectedDispute.currency})</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={resolution.refund_amount || ''}
                      onChange={(e) => setResolution({ ...resolution, refund_amount: parseFloat(e.target.value) })}
                      placeholder={`Full amount: ${selectedDispute.delivery_fee}`}
                    />
                  </div>
                )}

                {/* Resolution Notes */}
                <div className="space-y-2">
                  <Label>Resolution Notes *</Label>
                  <textarea
                    className="w-full min-h-[100px] px-3 py-2 border rounded-md"
                    placeholder="Explain the resolution decision..."
                    value={resolution.resolution_notes}
                    onChange={(e) => setResolution({ ...resolution, resolution_notes: e.target.value })}
                    required
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedDispute(null)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleResolveDispute}
                    disabled={resolving}
                    className="flex-1"
                  >
                    {resolving ? 'Resolving...' : 'Confirm Resolution'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDisputesPage;
